import type {
  RebookingCandidate,
  RebookingCandidateStatus,
  RiskKind,
  UnconfirmedRow,
} from '#services/api/mia.ts';

import { getConfirmationWorklist, listRebookingCandidates } from '#services/api/mia.ts';

const unknownProviderUserId = 0;
const rebookingAppointmentTypeId = 0;
const rebookingAppointmentTypeName = 'Appointment needing rebooking';

/**
 * The unconfirmed bucket card promises "Next 2 days" of appointments — keep the fetch window in
 * lockstep with that copy. (Matches the server default; explicit so the copy can't drift when
 * the default changes.)
 */
const unconfirmedWorklistDaysAhead = 2;

export interface RescheduleRow {
  id: number;
  startDate: string;
  eventAt: string;
  patientEmail: string | null;
  patientPhone: string | null;
  status: RebookingCandidateStatus;
  provider: {
    userId: number;
    firstName?: string;
    lastName?: string;
  };
  patient: {
    userId: number;
    firstName?: string;
    lastName?: string;
  };
  appointmentType: {
    id: number;
    name: string;
  };
}

export interface PatientReadinessDetailsResponse {
  unconfirmed: UnconfirmedRow[];
  reschedule: RescheduleRow[];
  /**
   * The org's derived risk kind, carried up from the worklist envelope so the briefing and the
   * details grid can phrase themselves in kind-aware copy.
   */
  riskKind: RiskKind;
}

export interface GetPatientReadinessDetailsParams {
  orgId: number;
}

export async function getPatientReadinessDetails(
  params: GetPatientReadinessDetailsParams
): Promise<PatientReadinessDetailsResponse> {
  const [worklistResponse, rebookingCandidatesResponse] = await Promise.all([
    getConfirmationWorklist({ orgId: params.orgId, daysAhead: unconfirmedWorklistDaysAhead }),
    listRebookingCandidates({ orgId: params.orgId }),
  ]);

  // Initial selection uses the returned order while the grid applies the same risk-first sort.
  // Keeping this at the fetch boundary avoids recalculating row order in the only consumer.
  return {
    unconfirmed: [...worklistResponse.rows].sort((left, right) => {
      const leftRisk = getUnconfirmedRowRiskSortValue(left);
      const rightRisk = getUnconfirmedRowRiskSortValue(right);

      if (leftRisk !== rightRisk) {
        return rightRisk - leftRisk;
      }

      return (
        new Date(left.appointmentStartsAt).getTime() - new Date(right.appointmentStartsAt).getTime()
      );
    }),
    reschedule: rebookingCandidatesResponse.candidates.map(mapRebookingCandidateToRescheduleRow),
    riskKind: worklistResponse.riskKind,
  };
}

/**
 * The row's predicted risk for whichever kind the org is scored on: only one of the two
 * probabilities is ever populated, so the coalesce picks it without needing the envelope's
 * `riskKind`. Null means unscored, which every caller must treat as unknown — not as low risk.
 */
export function getUnconfirmedRowRiskProbability(row: UnconfirmedRow): number | null {
  return row.predictedNoShowProbability ?? row.predictedLateCancelProbability;
}

/** Sorts below every real probability, which is bounded to 0..1. */
const unscoredRiskSortValue = -1;

/**
 * The row's risk as a sortable number, with unscored rows pushed last rather than treated as low
 * risk. Shared by this module's fetch-boundary sort and the details grid's risk column so the two
 * orderings can't drift apart.
 */
export function getUnconfirmedRowRiskSortValue(row: UnconfirmedRow): number {
  return getUnconfirmedRowRiskProbability(row) ?? unscoredRiskSortValue;
}

function mapRebookingCandidateToRescheduleRow(candidate: RebookingCandidate): RescheduleRow {
  return {
    id: candidate.appointmentId,
    startDate: candidate.appointmentTime,
    eventAt: candidate.eventAt,
    patientEmail: candidate.patientEmail,
    patientPhone: candidate.patientPhone,
    status: candidate.status,
    provider: {
      userId: unknownProviderUserId,
      firstName: candidate.providerFirstName ?? undefined,
      lastName: candidate.providerLastName ?? undefined,
    },
    patient: {
      userId: candidate.patientId,
      firstName: candidate.patientFirstName ?? undefined,
      lastName: candidate.patientLastName ?? undefined,
    },
    appointmentType: {
      id: rebookingAppointmentTypeId,
      name: rebookingAppointmentTypeName,
    },
  };
}
